import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export default class ThreeApp {
    /**
     * @param {Object} opts
     * @param {HTMLCanvasElement|string} opts.canvas
     *
     * @param {Object} [opts.camera]
     * @param {number} [opts.camera.fovy=60]
     * @param {number} [opts.camera.near=0.1]
     * @param {number} [opts.camera.far=100]
     * @param {THREE.Vector3} [opts.camera.position=new THREE.Vector3(0,0,2)]
     * @param {THREE.Vector3} [opts.camera.lookAt=new THREE.Vector3(0,0,0)]
     *
     * @param {Object} [opts.renderer]
     * @param {number} [opts.renderer.clearColor=0xffffff]
     * @param {boolean} [opts.renderer.transparent=true]
     *
     * @param {Object} [opts.controls]
     * @param {boolean} [opts.controls.enabled=true]
     * @param {number} [opts.controls.dampingFactor=0.1]
     * @param {number} [opts.controls.minDistance=2.0]
     * @param {number} [opts.controls.maxDistance=10.0]
     *
     * @param {Object} [opts.lights]
     * @param {Object} [opts.lights.directional]
     * @param {number} [opts.lights.directional.color=0xffffff]
     * @param {number} [opts.lights.directional.intensity=1.0]
     * @param {THREE.Vector3} [opts.lights.directional.position=new THREE.Vector3(2,2,2)]
     * @param {Object} [opts.lights.ambient]
     * @param {number} [opts.lights.ambient.color=0xffffff]
     * @param {number} [opts.lights.ambient.intensity=0.5]
     */
    constructor(opts = {}) {
        //Target
        this._canvas = typeof opts.canvas === "string" ? document.querySelector(opts.canvas) : opts.canvas || document.querySelector("#canvas");
        if (!this._canvas) {
            throw new Error("Canvas element not found. Pass a canvas element or selector.");
        }

        // Scene
        this.scene = new THREE.Scene();

        // Camera
        const w = window.innerWidth;
        const h = window.innerHeight;
        const camOpt = opts.camera || {};
        this.camera = new THREE.PerspectiveCamera(camOpt.fovy ?? 60, w / h, camOpt.near ?? 0.1, camOpt.far ?? 100.0);
        this.camera.position.copy(camOpt.position ?? new THREE.Vector3(0, 0, 2));
        this.camera.lookAt(camOpt.lookAt ?? new THREE.Vector3(0, 0, 0));

        // Renderer
        const rOpt = opts.renderer || {};
        this.renderer = new THREE.WebGLRenderer({
            canvas: this._canvas,
            alpha: rOpt.transparent ?? true,
        });
        if (rOpt.clearColor) {
            this.renderer.setClearColor(new THREE.Color(rOpt.clearColor ?? 0xffffff));
        }
        this.setSize(w, h);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

        // Controls
        const cOpt = opts.controls || null;
        if (cOpt) {
            this.controls = new OrbitControls(this.camera, this.renderer.domElement);
            this.controls.enabled = cOpt.enabled ?? true;
            this.controls.enableDamping = true;
            this.controls.dampingFactor = cOpt.dampingFactor ?? 0.1;
            this.controls.minDistance = cOpt.minDistance ?? 2.0;
            this.controls.maxDistance = cOpt.maxDistance ?? 10.0;
        } else {
            this.controls = null;
        }

        // Lights
        const lOpt = opts.lights || {};
        const dir = lOpt.directional || null;
        if (dir) {
            this.directionalLight = new THREE.DirectionalLight(dir.color ?? 0xffffff, dir.intensity ?? 1.0);
            this.directionalLight.position.copy(dir.position ?? new THREE.Vector3(2, 2, 2));
            this.scene.add(this.directionalLight);
        }

        const amb = lOpt.ambient || {};
        this.ambientLight = new THREE.AmbientLight(amb.color ?? 0xffffff, amb.intensity ?? 1.0);
        this.scene.add(this.ambientLight);

        // State
        // autoRender: false でループ内の renderer.render をスキップ(EffectComposer 等で外部描画する場合)
        this._autoRender = opts.autoRender ?? true;
        this._time = 0;
        this.clock = new THREE.Clock(false);
        this._raf = null;
        this._running = false;
        this._listeners = { update: new Set(), beforeRender: new Set(), afterRender: new Set(), resize: new Set() };

        // Resize handling
        this._resizeHandler = () => this._onResize();
        window.addEventListener("resize", this._resizeHandler, { passive: true });
    }

    /** Start the render loop. */
    start() {
        if (this._running) return;
        this._running = true;
        this.clock.start();

        const loop = () => {
            if (!this._running) return;
            this._raf = requestAnimationFrame(loop);

            const dt = this.clock.getDelta();
            this._time += dt;

            // 1) 複数登録できる update フック
            for (const fn of this._listeners.update) fn(this._time, dt, this);

            // 2) レンダリング前のフック
            for (const fn of this._listeners.beforeRender) fn(this._time, dt, this);
            if (this.controls) this.controls.update();
            if (this._autoRender) this.renderer.render(this.scene, this.camera);
            // 3) 描画後のフック
            for (const fn of this._listeners.afterRender) fn(this._time, dt, this);
        };

        this._raf = requestAnimationFrame(loop);
    }

    /** Stop the render loop. */
    stop() {
        this._running = false;
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = null;
        this.clock.stop();
    }

    /** Register hooks: "beforeRender" | "update" | "afterRender" | "resize" */
    on(name, fn) {
        const set = this._listeners?.[name];
        if (!set) throw new Error(`Unknown hook: ${name}`);
        set.add(fn);
        return () => this.off(name, fn);
    }
    off(name, fn) {
        const set = this._listeners?.[name];
        if (set) set.delete(fn);
    }

    /** Add any Object3D to the scene. Optionally set a rotationSpeed for auto-rotation on Z. */
    add(object3D) {
        this.scene.add(object3D);
        return object3D;
    }

    /** Remove an object from the scene. */
    remove(object3D) {
        this.scene.remove(object3D);
    }

    /** Resize renderer & camera. */
    setSize(width, height) {
        this.renderer.setSize(width, height, false);
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
    }

    _onResize() {
        const w = window.innerWidth;
        const h = window.innerHeight;
        this.setSize(w, h);
        for (const fn of this._listeners.resize) fn(w, h, this);
    }

    /** Dispose resources & listeners. */
    dispose() {
        this.stop();
        window.removeEventListener("resize", this._resizeHandler);

        this.scene.traverse((obj) => {
            if (obj.geometry) obj.geometry.dispose?.();
            if (obj.material) {
                if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose?.());
                else obj.material.dispose?.();
            }

            if (obj.texture) obj.texture.dispose?.();
        });
        this.renderer.dispose();
    }
}
